import {CdkTableDataSourceInput} from '@angular/cdk/table';
import {CommonModule, NgTemplateOutlet} from '@angular/common';
import {
    AfterContentInit,
    Component,
    ContentChild,
    ContentChildren,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    signal,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormBuilder, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatIconButton} from '@angular/material/button';
import {PageEvent} from '@angular/material/paginator';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable
} from '@angular/material/table';
import {Observable, Subscription} from 'rxjs';
import {ITableColumn} from '../../../core/common/interfaces/table-column.interface';
import {DataTableService} from '../../../core/services/data-table.service';
import {TableActionDirective} from './table-action.directive';
import {TableCellDirective} from './table-cell.directive';
import {NgxDatatableModule} from '@swimlane/ngx-datatable';
import {MatSelectModule} from '@angular/material/select';


import {MatSortModule, Sort} from '@angular/material/sort';
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {ActivatedRoute} from '@angular/router';
import {UserTableSettingsService} from '../../../core/services/user-table-settings.service';
import {CdkConnectedOverlay, CdkOverlayOrigin} from '@angular/cdk/overlay';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatIcon} from '@angular/material/icon';

@Component({
    selector: 'ostso-table',
    standalone: true,
    imports: [
        MatTable,
        MatHeaderCell,
        MatCell,
        MatHeaderRow,
        MatRow,
        MatColumnDef,
        MatHeaderRowDef,
        MatRowDef,
        MatCellDef,
        MatHeaderCellDef,
        NgTemplateOutlet,
        MatIconButton,
        MatSortModule,
        FormsModule,
        ReactiveFormsModule,
        NgxDatatableModule,
        MatSelectModule,
        CommonModule,
        CdkDropList,
        CdkDrag,
        CdkOverlayOrigin,
        CdkConnectedOverlay,
        MatCheckbox,
        MatIcon
    ],
    templateUrl: './table.component.html',
    styleUrl: './table.component.scss'
})
export class TableComponent<T> implements OnInit, AfterContentInit {

    @Input() filterValues?: any;
    @Input({
        required: true
    }) updateList?: Observable<void>;
    @Input() hasControls = true;

    @Input() set initialPager(v: {
        recordCount: number,
        pageSize: number
        page: number,
    })
    {
        this.recordCount = v.recordCount;
        this.pageSize = v.pageSize;
        this.page = v.page;
        this.form.patchValue({pageSizeControl: v.pageSize});
    }

    @ViewChild(MatTable, {read: ElementRef}) tableRef!: ElementRef<HTMLTableElement>;

    @Output() sortedColumn: EventEmitter<Sort> = new EventEmitter<Sort>();
    @Output() pageChange: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
    @Output() rowSelected = new EventEmitter<T>();
    @Output() changeColumns: EventEmitter<string[]> = new EventEmitter<string[]>();
    @ContentChildren(TableCellDirective) cellDirectives: QueryList<TableCellDirective> | undefined;
    @ContentChild(TableActionDirective) actionMenuDirective?: TableActionDirective;


    data: CdkTableDataSourceInput<T> = [];

    columns: SelectableTableColumn[] = [];

    displayedColumns: string[] = [];

    cellTemplates: ICellTemplates = {};
    actionTemplate?: TemplateRef<unknown>;

    recordCount = 25;
    pageSize = 10;

    page = 1;

    isStartResize = signal(false);


    protected readonly SETTINGS_COLUMN = SETTINGS_COLUMN;

    private subscription = new Subscription();

    form = this.fb.group({
        pageSizeControl: this.pageSize
    });

    // storage = inject(StorageService);
    protected isOpen: boolean = false;


    constructor(
        private dataTableService: DataTableService,
        private destroyRef: DestroyRef,
        private fb: FormBuilder,
        private renderer: Renderer2,
        private route: ActivatedRoute,
        private userTableSettingsService: UserTableSettingsService
    )
    {
    }

    ngOnInit(): void {
        this.userTableSettingsService.initComplete.subscribe(() => {
            if (!this.userTableSettingsService.initComplete.value) return;
            this.setColumns();
            this.initDataObserver();

            this.subscription.add(this.form.get('pageSizeControl')!.valueChanges.subscribe(value => {


                this.page = 1;
                this.pageSize = value!;

                const pageData: PageEvent = {
                    pageIndex: 1,
                    pageSize: value!,
                    length: this.recordCount
                };
                this.onPageChange(pageData);

            }));
        });

    }

    ngAfterContentInit(): void {
        this.initTemplates();
    }

    initDataObserver(): void {
        this.dataTableService.data$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
            if (data) {
                this.data = data.values;
                this.recordCount = data.pager?.countTotal;
            }
        });
    }

    setColumns(): void {
        this.columns = this.dataTableService.getColumns();

        if (this.dataTableService.showCustomColumns) {
            this.columns.push(
                {
                    name: SETTINGS_COLUMN,
                    sticky: true,
                    isRequired: true
                });

            this.updateSelectedColumns();
        }
        this.updateSelectedColumns();
    }

    changedSelectedColumn(): void {
        this.dataTableService.setCustomColumns(
            this.columns
                .filter(value => value.isSelected)
                .map(value => value.name)
        );

        this.updateSelectedColumns();

    }

    updateSelectedColumns(): void {

        const selectedColumns = this.dataTableService.getCustomColumns();
        this.columns.forEach(value => {
            value.isSelected = value.isRequired || value.default || selectedColumns.includes(value.name);
            value.default = false;
        });
        this.displayedColumns = this.columns
            .filter(value => value.isSelected)
            .map(value => value.name);


        this.displayedColumns = selectedColumns.map(value => this.displayedColumns.includes(value) ? value : null)
            .filter((value): value is string => value !== null);

        this.changeColumns.emit(this.displayedColumns);
    }

    initTemplates(): void {
        if (this.cellDirectives && this.cellDirectives.length) {
            this.cellTemplates = this.cellDirectives.reduce((acc: ICellTemplates, cur) => {
                acc[cur.name] = cur.template;
                return acc;
            }, {});
        }

        if (this.actionMenuDirective) {
            this.actionTemplate = this.actionMenuDirective.template;
        }


    }

    onPageChange(page: PageEvent): void {
        this.pageChange.emit(page);
    }

    setPage(page: any): void {
        this.page = page.page;
        const pageData: PageEvent = {
            pageIndex: page.page,
            pageSize: this.pageSize,
            length: this.recordCount
        };
        this.onPageChange(pageData);
    }

    sortChange($event: Sort): void {
        this.sortedColumn.emit($event);
        this.dataTableService.saveSorting($event);
    }

    drop(event: CdkDragDrop<string[]>): void {
        moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
        this.dataTableService.setCustomColumns(this.displayedColumns);
    }


    startResize(event: MouseEvent | TouchEvent, headerContent: HTMLElement, direction: 1 | -1 = 1, isTouch = false): void {
        event.preventDefault();
        event.stopPropagation();
        this.isStartResize.set(true);
        const headerCell = headerContent.closest('th');
        const startX = isTouch
            ? (event as TouchEvent).touches[0].clientX
            : (event as MouseEvent).clientX;
        const startWidth = headerCell!.getBoundingClientRect().width;

        const onMouseMove = (moveEvent: MouseEvent | TouchEvent): void => {
            const clientX = isTouch
                ? (moveEvent as TouchEvent).touches[0].clientX
                : (moveEvent as MouseEvent).clientX;
            const diff = clientX - startX;
            const newWidth = Math.max(50, startWidth + diff * direction);
            this.renderer.setStyle(headerCell, 'width', `${newWidth}px`);
        };

        const onMouseUp = (): void => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('touchmove', onMouseMove);
            document.removeEventListener('touchend', onMouseUp);
            setTimeout(() => {
                this.isStartResize.set(false);
            }, 200);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('touchmove', onMouseMove);
        document.addEventListener('touchend', onMouseUp);
    }

    onHeaderContentClick($event: MouseEvent): void {
        $event.stopPropagation();
        $event.preventDefault();
    }

    hasArchived(element: any): boolean {
        const res = element?.archive?.code === 'Archive';
        return res;
    }

    protected onBackdropClick(): void {
        this.isOpen = false;
    }
}

interface ICellTemplates {
    [key: string]: TemplateRef<unknown>;
}

export const SETTINGS_COLUMN = 'settings';

type SelectableTableColumn = ITableColumn & { isSelected?: boolean };
